import gridMixin from '../grid.mixin';
import { mapActions, mapState } from 'vuex';
import tmBus from 'v/Story/Timeline/api/tmBus';
import { AudioGhostMode, AudioTrackWrapper } from 'v/Story/Timeline/api/model';
import { TimePoint } from 'v/Story/Timeline/api/grid';
import {
  CursorManager,
  getAudioSnapTimesLeft,
  getAudioSnapTimesRight,
  assignDurations,
} from 'v/Story/Timeline/api/tapi';
import fg from '#/libs/FrameGrid';

export default {
  mixins: [gridMixin],

  computed: {
    ...mapState('timemachine', {
      MODE_SCENE_EDIT: (state) => state.MODE.SCENE_EDIT,
      MODE_OPTIONAL_AUDIO_INSERT: (state) => state.MODE.OPTIONAL_AUDIO_INSERT,
      MODE_AUDIO_MULTISELECT: (state) => state.MODE.AUDIO_MULTISELECT,
      pxAudioTrackH: (state) => state.cssVariables.pxAudioTrackH,
      trackLayer: (state) => state.trackLayer,
      secondhand: (state) => state.secondhand,
    }),

    moveObj() {
      return {
        enabled: this.$data.dragEnabled,
        distance2trigger: this.CONST.DND_SENSITIVITY.DEFAULT,
        onMoveStart: this.$_onMoveStart,
        onMove: this.$_onMove,
        onMoveEnd: this.$_onMoveEnd,
      };
    },
  },

  methods: {
    ...mapActions('timemachine', [
      'SET_MODE',
      'SET_DND_ESCORT',
      'SCENE_EDIT_MODE_APPLY',
      'INSERT_AUDIO',
      'MANAGED_AUDIO_INSERT',
      'MANAGED_AUDIO_INSERT_EXIT',
      'REMOVE_AUDIO',
      'UNSELECT_TRACKS',
      'SET_AUDIO_LAYER_DURATION',
      'RECALCULATE_ROWS',
      'AUDIO_MULTISELECT_EXIT',
    ]),

    $_onMoveStart(state) {
      if (this.MODE_SCENE_EDIT) {
        this.SCENE_EDIT_MODE_APPLY({ keepItSelected: false }).then(
          ({ sceneTrack }) => {
            tmBus.$emit('callbackApplySceneEdit', {
              e: state.eDown,
              asset: sceneTrack.data,
              analyticsData: { confirm: 'v', using: 'click' },
            });
          }
        );
      }
      if (this.MODE_OPTIONAL_AUDIO_INSERT) {
        this.MANAGED_AUDIO_INSERT_EXIT();
      }
      if (this.MODE_AUDIO_MULTISELECT) {
        this.AUDIO_MULTISELECT_EXIT();
      }

      this.UNSELECT_TRACKS();

      this.SET_MODE({ name: 'AUDIO_DND', value: true });
      this.SET_DND_ESCORT({ name: 'isExternal', value: true });
      this.$data.showGhost = true;

      state.mousePoint = new TimePoint();

      state.proximityTester = {
        left: this.$_createProximityTester(
          [
            this.secondhand,
            ...getAudioSnapTimesLeft({
              trackLayer: this.trackLayer,
            }),
          ],
          this.CONST.AUTOSNAP_PROXIMITY.AUDIO
        ),
        right: this.$_createProximityTester(
          [
            this.secondhand,
            ...getAudioSnapTimesRight({
              trackLayer: this.trackLayer,
            }),
          ],
          this.CONST.AUTOSNAP_PROXIMITY.AUDIO
        ),
      };

      state.track = new AudioTrackWrapper({
        selected: _.cloneDeep(this.audio),
        audioOnTrack: {
          /* placeholder */
        },
      });

      state.maxTrackEndTime = this.trackLayer.audioDuration;

      tmBus.$emit('callbackBeforeDragStarts');
      CursorManager.set('grabbing');
    },

    async $_onMove(state) {
      state.mousePoint.set(state.eMove.clientX, state.eMove.clientY);
      this.$data.isInside = this.axis.isInside(state.mousePoint);
      this.$data.ghostPoint.assign(state.mousePoint);

      if (this.$data.isInside) {
        this.$_toInternal(state.mousePoint);
        this.$_point2time(state.mousePoint);

        let time = fg.trunc(state.mousePoint.time);
        const snapTimeLeft = state.proximityTester.left.find(time);
        if (null !== snapTimeLeft) {
          time = snapTimeLeft;
        } else {
          const endTime = fg.trunc(time + state.track.durationLimits.max);
          const snapTimeRight = state.proximityTester.right.find(endTime);
          if (null !== snapTimeRight) {
            time = fg.trunc(snapTimeRight - state.track.durationLimits.max);
          }
        }

        assignDurations(state.track.data, {
          displayStartTime: time,
          displayDuration: state.track.durationLimits.max,
          displayEndTime: fg.trunc(time + state.track.durationLimits.max),
        });

        // reflect state on showGhost && mode
        const trackBellow = this.$_time2audioTrack(time);
        if (trackBellow) {
          this.$data.mode = AudioGhostMode.APPEND;
          this.$data.showGhost = true;
          this.SET_DND_ESCORT({ name: 'trackInHand', value: null });
        } else {
          this.$data.mode = AudioGhostMode.INSERT;
          this.$data.showGhost = false;
          this.SET_DND_ESCORT({ name: 'trackInHand', value: state.track });

          if (state.track.data.displayEndTime > state.maxTrackEndTime) {
            state.maxTrackEndTime = state.track.data.displayEndTime;
            this.SET_AUDIO_LAYER_DURATION(state.maxTrackEndTime);
            this.RECALCULATE_ROWS();
          }
        }

        state.time = time;
      } else {
        this.$data.mode = AudioGhostMode.DEFAULT;
        this.$data.showGhost = true;
        this.SET_DND_ESCORT({ name: 'trackInHand', value: null });
      }

      this.SET_DND_ESCORT({
        name: 'useScrollSensor',
        value: this.$data.isInside,
      });
    },

    async $_onMoveEnd(state) {
      CursorManager.restore();

      this.SET_AUDIO_LAYER_DURATION();
      this.RECALCULATE_ROWS();

      if (!state.canceled && this.$data.isInside) {
        if (this.$data.mode === AudioGhostMode.APPEND) {
          this.SET_DND_ESCORT({ name: 'trackInHand', value: null });
          this.INSERT_AUDIO({ track: state.track, appendToEnd: true }).then(
            ({ track }) => {
              tmBus.$emit('callbackChange', {
                e: state.eUp,
                seekToTime: track.data.displayStartTime,
              });
            }
          );
        } else if (this.$data.mode === AudioGhostMode.INSERT) {
          this.MANAGED_AUDIO_INSERT({
            track: state.track,
            time: state.time,
          }).then(({ track }) => {
            tmBus.$emit('callbackChange', {
              e: state.eUp,
              seekToTime: track.data.displayStartTime,
            });
          });
        }
      } else {
        this.SET_DND_ESCORT({ name: 'trackInHand', value: null });
      }

      this.SET_DND_ESCORT({ name: 'useScrollSensor', value: false });
      this.SET_DND_ESCORT({ name: 'isExternal', value: false });
      this.SET_MODE({ name: 'AUDIO_DND', value: false });
      this.$data.showGhost = false;
    },
  },
};
